import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Grid,
} from '@mui/material';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { candidateAPI } from '../../utils/api';
import useScreenShare from '../../hooks/useScreenShare';
import useRecording from '../../hooks/useRecording';
import { saveRecordingBackup, markRecordingAsUploaded, cleanupOldBackups } from '../../utils/recordingBackup';
import { getScreenShareCaptureInfo } from '../../utils/screenShareCapture';
import { scheduleTimeoutBeeps, playFinalBeep } from '../../utils/timeoutBeeps';
import { scheduleTimeoutNotifications, showFinalNotification } from '../../utils/timeoutNotifications';
import ProcessingStatusPolling from './ProcessingStatusPolling';
import RecordingTimerBar from './RecordingTimerBar';
import screenShareImage from '../../utils/img/Screen share tooltip.jpg';

const MAX_DURATION_MS = 15000; // 15 seconds

const TestRecordingView = ({ assignment, onBack }) => {
  const { t } = useTranslation();
  const [questionData, setQuestionData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoRecordingId, setVideoRecordingId] = useState(null);

  const beepsRef = useRef(null);
  const notificationsRef = useRef(null);
  const submittedRef = useRef(false);
  const screenShareCaptureInfoRef = useRef({
    display_surface: null,
    system_audio_captured: null,
  });

  const {
    screenShareActive,
    screenStream,
    error: screenShareError,
    detectScreenShare,
  } = useScreenShare();

  const {
    isRecording,
    elapsedTime,
    error: recordingError,
    startRecording,
    stopRecording,
  } = useRecording();

  // Add beforeunload handler to prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = 'You are currently recording. If you leave this page, your recording will be lost.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording]);

  // Cancel any pending warning/urgent beeps and notifications when the
  // component unmounts.
  useEffect(() => {
    return () => {
      if (beepsRef.current) {
        beepsRef.current.cancel();
        beepsRef.current = null;
      }
      if (notificationsRef.current) {
        notificationsRef.current.cancel();
        notificationsRef.current = null;
      }
    };
  }, []);

  const handleStartTest = async () => {
    setError('');
    setLoading(true);
    screenShareCaptureInfoRef.current = {
      display_surface: null,
      system_audio_captured: null,
    };

    try {
      // Request screen share
      const stream = await detectScreenShare();
      screenShareCaptureInfoRef.current = getScreenShareCaptureInfo(stream);

      // Fetch test question content
      const data = await candidateAPI.startTestQuestion(assignment.id, true);
      setQuestionData(data);

      // Reset submit guard for retries.
      submittedRef.current = false;

      // Start recording with 15-second max duration and auto-submit on timeout
      await startRecording(stream, {
        maxDuration: MAX_DURATION_MS,
        onAutoStop: () => handleSubmit(true, data) // Pass data directly to avoid closure issue
      });

      // Same audio cues and OS notifications as real questions.
      beepsRef.current = scheduleTimeoutBeeps(MAX_DURATION_MS);
      notificationsRef.current = scheduleTimeoutNotifications(MAX_DURATION_MS, {
        warningTitle: t('candidate.notifications.warningTitle'),
        warningBody: t('candidate.notifications.warningBody'),
        urgentTitle: t('candidate.notifications.urgentTitle'),
        urgentBody: t('candidate.notifications.urgentBody'),
      });
    } catch (err) {
      setError(err.message || t('candidate.testRecording.testError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (isAutoStop = false, testData = null) => {
    // Only check isRecording for manual clicks, not for auto-stop
    // Auto-stop already knows recording is active, no need to check state
    if (!isAutoStop && !isRecording) {
      alert(t('candidate.testRecording.noRecording'));
      return;
    }

    // Use passed testData (from auto-stop) or state questionData (from manual click)
    // This avoids closure issues where questionData might be null when callback is created
    const dataToUse = testData || questionData;

    if (!dataToUse) {
      setError('Question data not available');
      return;
    }

    // Guard against double-submit (auto-stop racing with a manual click).
    if (submittedRef.current) {
      return;
    }
    submittedRef.current = true;

    // Play the final cue BEFORE cancelling scheduled beeps, and only when time
    // actually ran out (not on manual submit). The notification mirrors the beep.
    if (isAutoStop) {
      playFinalBeep();
      showFinalNotification(
        t('candidate.notifications.finalTitle'),
        t('candidate.notifications.finalBody')
      );
    }

    if (beepsRef.current) {
      beepsRef.current.cancel();
      beepsRef.current = null;
    }
    if (notificationsRef.current) {
      notificationsRef.current.cancel();
      notificationsRef.current = null;
    }

    setUploading(true);

    try {
      // Stop recording and get blobs
      const { screenBlob, webcamBlob, audioBlob } = await stopRecording();

      // Validate that we have at least screen recording data
      if (!screenBlob || screenBlob.size === 0) {
        throw new Error('Screen recording failed - no data captured. Please try again.');
      }

      // Validate minimum file sizes (screen recording should be at least 1KB for any meaningful content)
      const minSize = 1024; // 1KB
      if (screenBlob.size < minSize) {
        throw new Error('Screen recording appears corrupted (file too small). Please try again.');
      }

      // Log recording sizes for debugging
      console.log('Test Recording sizes:', {
        screen: screenBlob ? `${(screenBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
        webcam: webcamBlob ? `${(webcamBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A',
        audio: audioBlob ? `${(audioBlob.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'
      });

      // Create form data
      const formData = new FormData();
      formData.append('assignment_id', assignment.id);
      formData.append('question_id', dataToUse.question_id);
      formData.append('comments', 'Test recording');
      const captureInfo = screenShareCaptureInfoRef.current;
      if (captureInfo.system_audio_captured !== null) {
        formData.append('system_audio_captured', String(captureInfo.system_audio_captured));
      }
      if (captureInfo.display_surface) {
        formData.append('display_surface', captureInfo.display_surface);
      }

      if (screenBlob) {
        formData.append('screen_recording', screenBlob, 'screen.webm');
      }
      if (webcamBlob) {
        formData.append('webcam_recording', webcamBlob, 'webcam.webm');
      }
      if (audioBlob) {
        formData.append('audio_recording', audioBlob, 'audio.webm');
      }

      // Backup to IndexedDB before upload
      let backupId;
      try {
        backupId = await saveRecordingBackup({
          assignment_id: assignment.id,
          question_id: dataToUse.question_id,
          screenBlob,
          webcamBlob,
          audioBlob,
          comments: 'Test recording'
        });
        console.log('Test recording backed up to IndexedDB before upload');
      } catch (backupError) {
        console.warn('Failed to backup test recording to IndexedDB:', backupError);
        // Continue with upload even if backup fails
      }

      // Upload test recording with progress tracking
      await candidateAPI.uploadTestRecording(formData, (progress) => {
        setUploadProgress(progress);
      });

      // Mark backup as uploaded
      if (backupId) {
        try {
          await markRecordingAsUploaded(backupId);
          // Cleanup old backups in background
          cleanupOldBackups().catch(err => console.warn('Cleanup failed:', err));
        } catch (markError) {
          console.warn('Failed to mark backup as uploaded:', markError);
        }
      }

      setShowVideo(true);
    } catch (err) {
      submittedRef.current = false;
      setError(t('candidate.testRecording.submitError') + ' ' + err.message);
      setUploading(false);
    }
  };

  const handleTryAgain = () => {
    // Reset all states
    setQuestionData(null);
    setShowVideo(false);
    setVideoRecordingId(null);
    setError('');
    submittedRef.current = false;
    screenShareCaptureInfoRef.current = {
      display_surface: null,
      system_audio_captured: null,
    };
    if (beepsRef.current) {
      beepsRef.current.cancel();
      beepsRef.current = null;
    }
    if (notificationsRef.current) {
      notificationsRef.current.cancel();
      notificationsRef.current = null;
    }
  };

  // Header chip shows remaining seconds alongside the shared timer bar below.
  const remainingTime = Math.max(0, MAX_DURATION_MS - elapsedTime);
  const formatTime = (ms) => (ms / 1000).toFixed(1);

  if (showVideo) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            {t('candidate.testRecording.complete')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleTryAgain}
            >
              {t('candidate.testRecording.tryAgain')}
            </Button>
            <Button variant="outlined" onClick={onBack}>
              {t('candidate.testRecording.backToQuestions')}
            </Button>
          </Box>
        </Box>

        <ProcessingStatusPolling
          assignmentId={assignment.id}
          onVideoReady={(recordingId) => setVideoRecordingId(recordingId)}
        />
      </Box>
    );
  }

  if (!questionData) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            {t('candidate.testRecording.title')}
          </Typography>
          <Button variant="outlined" onClick={onBack}>
            {t('candidate.testRecording.backToQuestions')}
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {screenShareError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {screenShareError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>{t('candidate.testRecording.practiceMode')}</strong> {t('candidate.testRecording.practiceModeDesc')}
            <br />
            <strong>{t('candidate.testRecording.shareWholeScreen')}</strong>
          </Alert>

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            {t('candidate.testRecording.beforeYouBegin')}
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.testRecording.shareEntireScreen')}
                    secondary={t('candidate.testRecording.shareEntireScreenDesc')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.testRecording.enableWebcam')}
                    secondary={t('candidate.testRecording.enableWebcamDesc')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('candidate.testRecording.enableMicrophone')}
                    secondary={t('candidate.testRecording.enableMicrophoneDesc')}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <img
                  src={screenShareImage}
                  alt="Screen sharing instructions"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ScreenShareIcon />}
              onClick={handleStartTest}
              disabled={loading}
            >
              {loading ? t('candidate.testRecording.starting') : t('candidate.testRecording.startButton')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">
            {t('candidate.testRecording.title')}
          </Typography>
          {isRecording && (
            <Chip
              icon={<FiberManualRecordIcon />}
              label={`${t('candidate.testRecording.recording')} (${formatTime(remainingTime)}s)`}
              color="error"
              sx={{ animation: 'pulse 2s infinite' }}
            />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {recordingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {recordingError}
        </Alert>
      )}

      {isRecording && (
        <RecordingTimerBar
          elapsedMs={elapsedTime}
          totalMs={MAX_DURATION_MS}
          unit="s"
          showChip={false}
          warningMessage={
            <>
              <strong>{t('candidate.testRecording.recording').toUpperCase()}:</strong> {t('candidate.testRecording.autoStopMessage')}
            </>
          }
        />
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('candidate.questionView.questionLabel')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
          {questionData.question_text}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<StopCircleIcon />}
          onClick={handleSubmit}
          disabled={!isRecording || uploading}
          fullWidth
        >
          {uploading ? t('candidate.testRecording.submitting') : t('candidate.testRecording.stopNow')}
        </Button>
      </Box>


      {uploading && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Uploading test recording... {uploadProgress}%
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">{uploadProgress}%</Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{t('candidate.testRecording.practiceMode')}</strong> {t('candidate.testRecording.practiceModeDesc')}
        </Alert>

        <Typography variant="subtitle2" color="text.secondary">
          {t('candidate.testRecording.autoStopMessage')}
        </Typography>
      </Paper>


    </Box>
  );
};

export default TestRecordingView;
